import type { AuthAccountLoginResponse } from 'api/auth'
import noop from 'lib/function/noop'
import { createReducer } from 'lib/redux/reducerUtils'
import { Dispatch, createContext } from 'react'
import { ValueOf } from 'type-fest'

// STATE
export enum BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS {
  VALIDATE_EMAIL,
  ACCOUNT_DETAILS,
  CREATE_PASSWORD,
  LINK_ACCOUNT,
  BUSINESS_EXISTS,
}

export interface BusinessTravellerBusinessOnboardingState {
  businessName: string
  adminEmail: string
  accountDetails?: App.BusinessTraveller.BusinessSignupFormData
  businessTravellerAccount?: AuthAccountLoginResponse
  creditCustomerAccessToken?: string
  creditCustomerEmail?: string
  creditCustomerId?: string
  step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS
}

const BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_INITIAL_STATE: BusinessTravellerBusinessOnboardingState = {
  businessName: '',
  adminEmail: '',
  step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.VALIDATE_EMAIL,
}

export const BusinessTravellerBusinessOnboardingStateContext =
  createContext<BusinessTravellerBusinessOnboardingState>(BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_INITIAL_STATE)

// DISPATCH
export const BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO = 'BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO'
export const BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_BUSINESS_AND_EMAIL = 'BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_BUSINESS_AND_EMAIL'
export const BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_ACCOUNT_DETAILS = 'BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_ACCOUNT_DETAILS'
export const BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_CREDIT_ACCOUNT = 'BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_CREDIT_ACCOUNT'

export type BusinessTravellerBusinessOnboardingDispatchAction = ValueOf<Utils.FullActionMap<{
  [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO]: {
    step?: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS
  },
  [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_BUSINESS_AND_EMAIL]: {
    businessName?: string
    adminEmail?: string
  },
  [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_ACCOUNT_DETAILS]: {
    accountDetails?: App.BusinessTraveller.BusinessSignupFormData
  },
  [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_CREDIT_ACCOUNT]: {
    businessTravellerAccount?: AuthAccountLoginResponse
    creditCustomerAccessToken?: string
    creditCustomerEmail?: string
    creditCustomerId?: string
  },
}>>

export const businessTravellerBusinessOnboardingStateReducer = createReducer<BusinessTravellerBusinessOnboardingState, BusinessTravellerBusinessOnboardingDispatchAction>(
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_INITIAL_STATE,
  {
    [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO]: (state, action) => ({
      step: action.step,
    }),
    [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_BUSINESS_AND_EMAIL]: (state, action) => ({
      businessName: action.businessName,
      adminEmail: action.adminEmail,
      ...(action.adminEmail !== state.adminEmail ? {
        businessTravellerAccount: undefined, creditCustomerAccessToken: undefined, creditCustomerEmail: undefined, creditCustomerId: undefined,
      } : {}),
    }),
    [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_ACCOUNT_DETAILS]: (state, action) => ({
      accountDetails: action.accountDetails,
    }),
    [BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_CREDIT_ACCOUNT]: (state, action) => ({
      businessTravellerAccount: action.businessTravellerAccount,
      creditCustomerAccessToken: action.creditCustomerAccessToken,
      creditCustomerEmail: action.creditCustomerEmail,
      creditCustomerId: action.creditCustomerId,
    }),
  },
)

export const BusinessTravellerBusinessOnboardingDispatchContext =
  createContext<Dispatch<BusinessTravellerBusinessOnboardingDispatchAction>>(noop)
